import { Component, OnInit } from '@angular/core';
import { SimpleModalComponent } from '@looorent/ngx-simple-modal';
import { EnforcementService, VoidReasonResponse } from 'src/app/Services/enforcement.service';

export interface VoidInfringementModalModel {
  infringementId: number;
  infringementTypeName: string;
}

export interface VoidInfringementModalResult {
  voidReasonId: number;
  comment: string;
}

@Component({
    selector: 'app-void-infringement-modal',
    template: `
    <div class="modal is-active">
      <div class="modal-background"></div>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            <span class="icon mr-2"><i class="fas fa-ban"></i></span>
            Void Infringement
          </p>
        </header>
        <section class="modal-card-body">
          <div class="notification is-warning is-light mb-4">
            <p>
              <strong>You are about to void:</strong> {{infringementTypeName}}
            </p>
            <p class="is-size-7 mt-1">
              This action will set the infringement amount to $0 and may affect the citation status.
            </p>
          </div>
    
          <div class="field">
            <label class="label">Void Reason <span class="has-text-danger">*</span></label>
            <div class="control">
              <div class="select is-fullwidth" [class.is-loading]="loadingReasons">
                <select [(ngModel)]="selectedVoidReasonId">
                  <option [ngValue]="null">-- Select a void reason --</option>
                  @for (reason of voidReasons; track reason) {
                    <option [ngValue]="reason.Id">
                      {{reason.Text}}
                    </option>
                  }
                </select>
              </div>
            </div>
            @if (selectedVoidReason?.Description) {
              <p class="help">
                {{selectedVoidReason?.Description}}
              </p>
            }
          </div>
    
          <div class="field">
            <label class="label">Comment</label>
            <div class="control">
              <textarea
                class="textarea"
                [(ngModel)]="comment"
                placeholder="Enter any additional notes about why this infringement is being voided..."
                rows="3">
              </textarea>
            </div>
            <p class="help">Optional - provide additional context for this void action</p>
          </div>
        </section>
        <footer class="modal-card-foot">
          <div class="buttons is-right" style="width: 100%;">
            <button class="button" (click)="close()">Cancel</button>
            <button class="button is-danger" (click)="confirm()" [disabled]="!canConfirm()">
              <span class="icon"><i class="fas fa-ban"></i></span>
              <span>Void Infringement</span>
            </button>
          </div>
        </footer>
      </div>
    </div>
    `,
    styles: [`
    .modal-card {
      max-width: 550px;
      width: 90%;
    }
    .modal-card-body {
      padding: 1.5rem;
    }
    .textarea {
      resize: vertical;
      min-height: 80px;
    }
  `],
    standalone: false
})
export class VoidInfringementModalComponent extends SimpleModalComponent<VoidInfringementModalModel, VoidInfringementModalResult> implements OnInit {
  infringementId: number = 0;
  infringementTypeName: string = '';
  
  voidReasons: VoidReasonResponse[] = [];
  loadingReasons: boolean = false;
  selectedVoidReasonId: number | null = null;
  comment: string = '';

  constructor(private enforcementService: EnforcementService) {
    super();
  }

  async ngOnInit() {
    this.loadingReasons = true;
    try {
      this.voidReasons = await this.enforcementService.getVoidReasons();
    } catch (error) {
      console.error('Failed to load void reasons:', error);
    } finally {
      this.loadingReasons = false;
    }
  }

  get selectedVoidReason(): VoidReasonResponse | undefined {
    return this.voidReasons.find(r => r.Id === this.selectedVoidReasonId);
  }

  canConfirm(): boolean {
    return this.selectedVoidReasonId != null && !this.loadingReasons;
  }

  confirm() {
    if (this.canConfirm()) {
      this.result = { 
        voidReasonId: this.selectedVoidReasonId!,
        comment: this.comment.trim()
      };
      this.close();
    }
  }

  override close(): Promise<any> {
    return super.close();
  }
}

